import type { StyleRow } from './stylesFixtureData';
import { seasons, groups, brandOptions, suppliers, contactPersons } from './stylesFixtureData';

/**
 * Fixtures for the DL-66682 epic (dl-57695, Testmo folder 93168 part 8 of 9 — CF Date edge cases,
 * AllowedValue (enum) filters, Hierarchy/NestedAllowedValue custom fields). Real, persisted
 * `stylesData` rows only — never `apiTransform` injection (DL-64371). Additive: appended on top of
 * every existing generated + sibling-epic fixture row. Ids 99118+ (the DL-66669 epic reserved and
 * used 99100-99117).
 *
 * Reuses two EXISTING CF definitions (`stylesCustomFieldDefinitions.ts`) rather than adding new
 * ones:
 *  - CF 703 "Fit Type" (`AllowedValue`, single-select, `cf_703_dd`) — values Slim/7031,
 *    Regular/7032, Loose/7033. Grepped the whole `src/mocks` tree: no existing fixture anywhere
 *    ever sets CF 703 to Loose/7033 — it is genuinely a defined-but-never-used value across the
 *    WHOLE shared dataset, so it doubles as DL-66689's "unused value" probe with no new CF needed.
 *  - CF 705/706 ("Print Type" / "Print Placement", `NestedAllowedValue`, `cf_705_dd`/`cf_706_dd`) —
 *    706 is a CHILD field of 705 (`parentCustomFieldId: 705`), each own allowed value independently
 *    filterable via its own `cf_<id>_dd` param. This is the real shape of "Hierarchy" in this
 *    codebase: a CHAIN of separate NestedAllowedValue fields (cascading dropdowns), not one field
 *    with self-nested values — see this epic's PR description for the full investigation writeup.
 *
 * ## Row groups
 *
 *  - **AllowedValue** (CF 703, ids 99118-99120) — DL-66687/66688/66689/66690/66691. `slim` +
 *    `regular` discriminate single-value match (66687) and OR (66688/66690); `noneSet` is the
 *    null-handling control. `Loose`/7033 deliberately appears on NO row here (see header) — the
 *    dropdown-lists-all-values probe (66689) and its dependent no-match probe (66691) both rely on
 *    it staying entirely unused.
 *  - **Hierarchy** (CF 705 + 706, ids 99121-99125) — DL-66692/66693/66694. `chestLeafTarget` +
 *    `sleeveLeafSecond` (both CF705=Screen Print/7051 + a CF706 child) discriminate the leaf-level
 *    OR (66694); `backLeafDecoy` (CF705=Embroidery/7052 + its own child) is the disjoint control.
 *    `childOnlyNoParentSet` (CF706 set, CF705 absent) + `parentOnlyNoChildSet` (CF705 set, CF706
 *    absent) are the pair that answers the "parent node" investigation (DL-66693): filtering the
 *    PARENT field (`cf_705_dd=7051`) must match `parentOnlyNoChildSet` (its own CF705 literally
 *    equals 7051) but must NOT match `childOnlyNoParentSet` (whose CF706 is a child of 7051, but
 *    whose OWN CF705 entry is absent) — proving the filter is an exact, per-field match with no
 *    cross-field descendant expansion.
 */

let nextFixtureId = 99118;
const nextId = (): number => nextFixtureId++;

const baseRow = (overrides: Partial<StyleRow> & { name: string; customId: string }): StyleRow => ({
  id: nextId(),
  description: null,
  state: 'published',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-04-01T09:00:00',
  readyForExport: false,
  ...overrides,
});

// ---------------------------------------------------------------------------------------------
// AllowedValue — CF 703 "Fit Type" (single-select, `cf_703_dd`). Prefixed "CFENUM".
// ---------------------------------------------------------------------------------------------
export const CF_ENUM_CUSTOM_IDS = {
  slim: 'CFENUM-01',
  regular: 'CFENUM-02',
  noneSet: 'CFENUM-03',
};

export const CF_ENUM_ALLOWED_VALUE_IDS = {
  slim: 7031,
  regular: 7032,
  looseUnused: 7033,
};

// ---------------------------------------------------------------------------------------------
// Hierarchy — CF 705 "Print Type" (mother, `cf_705_dd`) + CF 706 "Print Placement" (child of 705,
// `cf_706_dd`). Prefixed "CFHIER".
// ---------------------------------------------------------------------------------------------
export const CF_HIER_CUSTOM_IDS = {
  chestLeafTarget: 'CFHIER-01', // CF705=Screen Print(7051), CF706=Chest(7061)
  sleeveLeafSecond: 'CFHIER-02', // CF705=Screen Print(7051), CF706=Sleeve(7062)
  backLeafDecoy: 'CFHIER-03', // CF705=Embroidery(7052), CF706=Back(7063)
  childOnlyNoParentSet: 'CFHIER-04', // CF706=Chest(7061) only, CF705 NOT set
  parentOnlyNoChildSet: 'CFHIER-05', // CF705=Screen Print(7051) only, CF706 NOT set
};

export const CF_HIER_ALLOWED_VALUE_IDS = {
  screenPrint: 7051,
  embroidery: 7052,
  chest: 7061,
  sleeve: 7062,
  back: 7063,
};

export const buildCfEnumHierarchyFixtureStyles = (): StyleRow[] => [
  baseRow({
    name: 'CF Enum Slim',
    customId: CF_ENUM_CUSTOM_IDS.slim,
    customFields: [
      {
        id: 703,
        type: 'allowedvalue',
        value: { id: CF_ENUM_ALLOWED_VALUE_IDS.slim, value: 'Slim' },
      },
    ],
  }),
  baseRow({
    name: 'CF Enum Regular',
    customId: CF_ENUM_CUSTOM_IDS.regular,
    customFields: [
      {
        id: 703,
        type: 'allowedvalue',
        value: { id: CF_ENUM_ALLOWED_VALUE_IDS.regular, value: 'Regular' },
      },
    ],
  }),
  baseRow({
    name: 'CF Enum None Set',
    customId: CF_ENUM_CUSTOM_IDS.noneSet,
    customFields: [],
  }),

  baseRow({
    name: 'CF Hierarchy Chest Leaf Target',
    customId: CF_HIER_CUSTOM_IDS.chestLeafTarget,
    customFields: [
      {
        id: 705,
        type: 'allowedvalue',
        value: { id: CF_HIER_ALLOWED_VALUE_IDS.screenPrint, value: 'Screen Print' },
      },
      {
        id: 706,
        type: 'allowedvalue',
        value: { id: CF_HIER_ALLOWED_VALUE_IDS.chest, value: 'Chest' },
      },
    ],
  }),
  baseRow({
    name: 'CF Hierarchy Sleeve Leaf Second',
    customId: CF_HIER_CUSTOM_IDS.sleeveLeafSecond,
    customFields: [
      {
        id: 705,
        type: 'allowedvalue',
        value: { id: CF_HIER_ALLOWED_VALUE_IDS.screenPrint, value: 'Screen Print' },
      },
      {
        id: 706,
        type: 'allowedvalue',
        value: { id: CF_HIER_ALLOWED_VALUE_IDS.sleeve, value: 'Sleeve' },
      },
    ],
  }),
  baseRow({
    name: 'CF Hierarchy Back Leaf Decoy',
    customId: CF_HIER_CUSTOM_IDS.backLeafDecoy,
    customFields: [
      {
        id: 705,
        type: 'allowedvalue',
        value: { id: CF_HIER_ALLOWED_VALUE_IDS.embroidery, value: 'Embroidery' },
      },
      {
        id: 706,
        type: 'allowedvalue',
        value: { id: CF_HIER_ALLOWED_VALUE_IDS.back, value: 'Back' },
      },
    ],
  }),
  baseRow({
    name: 'CF Hierarchy Child Only No Parent Set',
    customId: CF_HIER_CUSTOM_IDS.childOnlyNoParentSet,
    customFields: [
      {
        id: 706,
        type: 'allowedvalue',
        value: { id: CF_HIER_ALLOWED_VALUE_IDS.chest, value: 'Chest' },
      },
    ],
  }),
  baseRow({
    name: 'CF Hierarchy Parent Only No Child Set',
    customId: CF_HIER_CUSTOM_IDS.parentOnlyNoChildSet,
    customFields: [
      {
        id: 705,
        type: 'allowedvalue',
        value: { id: CF_HIER_ALLOWED_VALUE_IDS.screenPrint, value: 'Screen Print' },
      },
    ],
  }),
];
